import { ButtonGroup, Grid } from '@mui/material';
import { HSpacer, RoutalPalette, VSpacer } from '@nx-smartmonkey/ui';
import { useEffect, useState } from 'react';

import { useIntl } from 'react-intl';
import InfoIcon from '../../../assets/img/info-icon.svg';
import BulletPointOrange from '../../../assets/img/subscription/bullet-point-orange.svg';
import { currencyToSymbol } from '../../../constants/Pricing';
import { SMARTMONKEY_TO_ROUTAL_NAMES } from '../../../constants/Subscriptions';
import { BillingPeriod, PlanType } from '../../../domain/subscription/Subscription';
import { useCreateOrganizationPaymentLink } from '../../../hooks/organization/useCreateOrganizationPaymentLink';
import { useRetrieveSubscriptionPricing } from '../../../hooks/subscription/useRetrieveSubscriptionPricing';
import Button from '../../../react-components/Button';
import Text from '../../../react-components/Text';
import { Pricing } from '../../../repositories/subscription/SubscriptionRepository';

interface SubscriptionPlanContainerProps {
  id: PlanType;
  label: string;
  items: string[];
  subscribeButtonEnabled?: boolean;
  pricing?: Pricing;
  billingPeriod: BillingPeriod;
  onSubscribe: (plan: PlanType) => void;
}

const SubscriptionPlanContainer = ({
  id,
  label,
  items,
  billingPeriod,
  pricing,
  subscribeButtonEnabled = true,
  onSubscribe,
}: SubscriptionPlanContainerProps) => {
  const intl = useIntl();
  return (
    <Grid
      item
      container
      direction="column"
      wrap="nowrap"
      style={{
        minHeight: `420px`,
        height: `100%`,
        width: `360px`,
        border: `1px solid ${RoutalPalette.grey4}`,
        borderRadius: `4px`,
        padding: `20px`,
        position: `relative`,
        paddingBottom: `90px`,
      }}
    >
      <Text variant="h5" isBold style={{ color: RoutalPalette.primary40 }}>
        {label}
      </Text>

      <VSpacer medium />

      <Grid item container direction="row" wrap="nowrap" alignItems="flex-end" justifyContent="flex-start">
        {!pricing || billingPeriod !== pricing?.billing_period ? (
          <Text variant="label" intlId="loading" style={{ fontSize: `12px`, textTransform: `lowercase` }} />
        ) : (
          <>
            <Text variant="h6" isBold>
              {`${pricing.vehicles[id]}${currencyToSymbol(pricing.currency)} ${intl
                .formatMessage({
                  id: `subscription.update.per_vehicle`,
                })
                .toLowerCase()}`}
            </Text>
            {billingPeriod === `month` ? (
              <Text
                variant="label"
                style={{
                  marginLeft: `2px`,
                  marginBottom: `2px`,
                  textTransform: `lowercase`,
                }}
                intlId="subscription.update.per_month"
              />
            ) : null}
          </>
        )}
      </Grid>

      <VSpacer large />

      <Grid item container direction="column" wrap="nowrap">
        {items.map((item, index) => (
          <>
            <Grid item container direction="row" wrap="nowrap" alignItems="center" justifyContent="flex-start">
              <img src={BulletPointOrange} alt="" draggable="false" style={{ width: `18px`, marginRight: `8px` }} />
              <Text intlId={item} />
            </Grid>

            {index !== items.length - 1 ? <VSpacer medium /> : null}
          </>
        ))}
      </Grid>

      <Grid
        container
        style={{
          position: `absolute`,
          bottom: `20px`,
          left: 0,
          width: `100%`,
          padding: `0 20px`,
        }}
      >
        <Button
          style={{
            height: `36px`,
            width: `100%`,
            cursor: !subscribeButtonEnabled ? `default` : `pointer`,
          }}
          size="xs"
          disabled={!subscribeButtonEnabled}
          onClick={() => {
            if (subscribeButtonEnabled) {
              onSubscribe(id);
            }
          }}
        >
          <Text style={{ color: RoutalPalette.oldWhite }} intlId={`subscription.update.subscribe`}></Text>
        </Button>
      </Grid>
    </Grid>
  );
};

export const NoSubscriptionPricingTable = () => {
  const [loadingSubscriptionPlan, setLoadingSubscriptionPlan] = useState<PlanType | undefined>(undefined);
  const [billingPeriod, setBillingPeriod] = useState<BillingPeriod>(`month`);
  const { isLoading, data: paymentLink, run: createPaymentLink } = useCreateOrganizationPaymentLink();

  const {
    data: pricing,
    isLoading: loadingPrices,
    run: getPricing,
  } = useRetrieveSubscriptionPricing({ billingPeriod });

  useEffect(() => {
    if (pricing?.billing_period !== billingPeriod) {
      getPricing();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [billingPeriod]);

  useEffect(() => {
    if (paymentLink) {
      window.open(paymentLink, `_self`);
    }

    return () => {
      setLoadingSubscriptionPlan(undefined);
    };
  }, [paymentLink]);

  return (
    <Grid container direction="column" wrap="nowrap" style={{ height: `100%`, position: `relative` }}>
      <Grid container style={{ position: `absolute`, top: `20px`, left: `20px` }}>
        <img alt="" src={InfoIcon} width="14px" draggable="false" style={{ marginRight: `4px` }} />
        <Text
          variant="button"
          intlId="subscription.update.info"
          isBold
          onClick={() => {
            window.open(`https://routal.com/pricing`, `_blank`);
          }}
        />
      </Grid>

      <Grid item container direction="row" alignItems="center" justifyContent="center" style={{ marginTop: `120px` }}>
        <Grid item>
          <ButtonGroup disableElevation disableRipple disableFocusRipple variant="contained">
            <Button
              style={{
                marginRight: `1px`,
                borderBottomRightRadius: 0,
                borderTopRightRadius: 0,
                width: `120px`,
                backgroundColor: billingPeriod === `month` ? RoutalPalette.primary40 : RoutalPalette.grey2,
                color: billingPeriod === `month` ? `white` : RoutalPalette.text,
              }}
              intlId="subscription.update.monthly"
              onClick={() => setBillingPeriod(`month`)}
            />

            <Button
              style={{
                padding: `4px 8px`,
                borderBottomLeftRadius: 0,
                borderTopLeftRadius: 0,
                width: `120px`,
                backgroundColor: billingPeriod === `year` ? RoutalPalette.primary40 : RoutalPalette.grey2,
                color: billingPeriod === `year` ? `white` : RoutalPalette.text,
              }}
              intlId="subscription.update.yearly"
              onClick={() => setBillingPeriod(`year`)}
            />
          </ButtonGroup>
        </Grid>
      </Grid>

      <VSpacer large />
      <VSpacer large />
      <VSpacer large />

      <Grid item container direction="row" wrap="nowrap" alignItems="center" justifyContent="center">
        <SubscriptionPlanContainer
          id="monkey"
          label={SMARTMONKEY_TO_ROUTAL_NAMES[`monkey`]}
          items={[
            `subscription.update.monkey.feature.1`,
            `subscription.update.monkey.feature.2`,
            `subscription.update.monkey.feature.3`,
            `subscription.update.monkey.feature.4`,
            `subscription.update.monkey.feature.5`,
            `subscription.update.monkey.feature.6`,
          ]}
          billingPeriod={billingPeriod}
          pricing={!loadingPrices ? pricing : undefined}
          subscribeButtonEnabled={!isLoading || loadingSubscriptionPlan !== `monkey`}
          onSubscribe={(plan) => {
            setLoadingSubscriptionPlan(plan);
            createPaymentLink({
              plan,
              billingPeriod,
            });
          }}
        />

        <HSpacer large />

        <SubscriptionPlanContainer
          id="gorilla"
          label={SMARTMONKEY_TO_ROUTAL_NAMES[`gorilla`]}
          items={[
            `subscription.update.gorilla.feature.1`,
            `subscription.update.gorilla.feature.2`,
            `subscription.update.gorilla.feature.3`,
            `subscription.update.gorilla.feature.4`,
            `subscription.update.gorilla.feature.5`,
            `subscription.update.gorilla.feature.6`,
          ]}
          billingPeriod={billingPeriod}
          pricing={!loadingPrices ? pricing : undefined}
          subscribeButtonEnabled={!isLoading || loadingSubscriptionPlan !== `gorilla`}
          onSubscribe={(plan) => {
            setLoadingSubscriptionPlan(plan);
            createPaymentLink({
              plan,
              billingPeriod,
            });
          }}
        />

        <HSpacer large />

        <SubscriptionPlanContainer
          id="kong"
          label={SMARTMONKEY_TO_ROUTAL_NAMES[`kong`]}
          items={[
            `subscription.update.kong.feature.1`,
            `subscription.update.kong.feature.2`,
            `subscription.update.kong.feature.3`,
            `subscription.update.kong.feature.4`,
            `subscription.update.kong.feature.5`,
          ]}
          billingPeriod={billingPeriod}
          pricing={!loadingPrices ? pricing : undefined}
          subscribeButtonEnabled={!isLoading || loadingSubscriptionPlan !== `kong`}
          onSubscribe={(plan) => {
            setLoadingSubscriptionPlan(plan);
            createPaymentLink({
              plan,
              billingPeriod,
            });
          }}
        />
      </Grid>
    </Grid>
  );
};
